import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { file_06 } from "@tql/ui-components-react/untitled/icons";

import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { LoaderCell } from "~/shared/components/trax-grid/loader-cell/loader-cell.tsx";
import { TrackingCell } from "~/shared/components/trax-grid/tracking-cell/tracking-cell.tsx";
import type { Load } from "~/shared/schemas/loads-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types";

import { LoadActionsCell } from "../components/load-actions-cell/load-actions-cell.tsx";
import { useLoadCellContext } from "./load-cell-context.tsx";

type BuildLoadIconColumnsArgs = {
	isInternal: boolean;
};

// Stable, module-scope cell renderers. Their function references never
// change, so React keeps cells mounted across column-pref churn instead of
// unmounting and remounting every cell on column reorder.
const TrackingCellRenderer = (props: GridCustomCellProps) => {
	const { isLoading } = useLoadCellContext();
	return isLoading ? <LoaderCell {...props} /> : <TrackingCell {...props} />;
};

const ActionsCell = (props: GridCustomCellProps) => {
	const { isLoading, onSubscribeToNotifications } = useLoadCellContext();
	if (isLoading) return <LoaderCell {...props} />;
	return (
		<LoadActionsCell
			onSubscribeToNotifications={
				onSubscribeToNotifications ?? (() => undefined)
			}
			{...props}
		/>
	);
};

const DocsCell = (props: GridCustomCellProps) => {
	const { isLoading, onDocumentClick } = useLoadCellContext();
	if (isLoading) return <LoaderCell {...props} />;
	return (
		<GridTd tdProps={props.tdProps}>
			<Button
				aria-label={"Documents"}
				onClick={() => {
					onDocumentClick?.(props.dataItem as Load);
				}}
				startIcon={<SvgIcon icon={file_06} size={"large"} />}
				variant={"tertiary"}
			/>
		</GridTd>
	);
};

/**
 * Builds the leading "icon" columns for the Loads grid. These have no `field`
 * value, are not in `defaultShipmentBoardGridPrefs`, and so their visibility,
 * width, and order are controlled directly here. Internal shows a tracking
 * icon; external shows notifications + docs icons.
 */
export const buildLoadIconColumns = ({
	isInternal,
}: BuildLoadIconColumnsArgs): BoardColumn[] => {
	if (isInternal) {
		return [
			{
				field: "tracking",
				title: "TRK",
				menu: false,
				show: true,
				width: 40,
				filterable: false,
				required: true,
				resizable: false,
				reorderable: false,
				cells: {
					data: TrackingCellRenderer,
				},
			},
		];
	}

	return [
		{
			field: "",
			title: "",
			menu: false,
			show: true,
			width: 52,
			filterable: false,
			reorderable: false,
			cells: {
				data: ActionsCell,
			},
		},
		{
			field: "",
			title: "Docs",
			menu: false,
			show: true,
			width: 50,
			filterable: false,
			reorderable: false,
			cells: {
				data: DocsCell,
			},
		},
	];
};
